// Small action dropdown used on multi-family CRUD rows. Click-away
// dismiss, keyboard a11y, and a fixed `aria-haspopup="menu"` outer
// button.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '@ng-unifi/ui';

export interface RowAction {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  hidden?: boolean;
  disabled?: boolean;
}

export function RowActions({ actions }: { actions: RowAction[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    function away(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  const visible = actions.filter((a) => !a.hidden);
  if (visible.length === 0) return null;
  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-surface-container-high"
      >
        <Icon name="more_vert" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 w-48 bg-surface-container-lowest border border-outline-variant rounded-lg shadow-lg z-30 py-1"
        >
          {visible.map((a) => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              disabled={a.disabled}
              onClick={() => {
                if (a.disabled) return;
                setOpen(false);
                a.onClick();
              }}
              className={`w-full text-left px-3 py-2 font-body-md text-body-md hover:bg-surface-container-low disabled:cursor-not-allowed disabled:opacity-50 ${
                a.destructive ? 'text-error' : 'text-on-surface'
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function Toast({
  message,
  onDismiss,
  extra,
}: {
  message: string;
  onDismiss: () => void;
  extra?: ReactNode;
}) {
  return (
    <div
      role="status"
      className="bg-secondary-container text-on-secondary-container rounded-lg px-4 py-2 font-body-md text-body-md flex items-center gap-2"
    >
      <Icon name="check_circle" />
      <span className="flex-1">{message}</span>
      {extra}
      <button
        type="button"
        onClick={onDismiss}
        className="underline font-medium"
      >
        Dismiss
      </button>
    </div>
  );
}
